import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useBeforeUnload } from 'react-router-dom';
import { ArrowLeft, Save, RotateCcw, ImageOff, Lock } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Card } from '../../../components/Shared/Card';
import { Button } from '../../../components/Shared/Button';
import { Badge } from '../../../components/Shared/Badge';
import { Tooltip } from '../../../components/Shared/Tooltip';
import { Loading } from '../../../components/Shared/Loading';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { FormField } from '../../../components/Shared/FormField';
import { Checkbox } from '../../../components/Shared/Checkbox';
import { useTenantSettingsStore } from '../../../store/useTenantSettingsStore';
import { useTimeClockPolicyStore } from '../../../store/useTimeClockPolicyStore';
import { TimeClockPolicySection } from './TimeClockPolicySection';
import { VehicleSettingsSection } from './VehicleSettingsSection';
import { DEFAULT_VEHICLE_SETTINGS, vehicleSettingsFromTenant, vehicleSettingsPayload, type VehicleSettingsForm } from './vehicleSettingsForm';
import { Grid, Section } from './SettingsSection';
import {
  formToPolicy,
  hasPolicyErrors,
  policyFormsEqual,
  policyToForm,
  validatePolicyForm,
  type PolicyErrors,
  type PolicyForm,
} from './timeClockPolicyForm';
import {
  EMAIL_PATTERN,
  HEX_COLOR_PATTERN,
  LANGUAGE_SUGGESTIONS,
  NUMBER_START_MAX,
  NUMBER_START_MIN,
  PERSONAL_NUMBER_SEQUENCE_MAX,
  PERSONAL_NUMBER_SEQUENCE_MIN,
  TENANT_SETTINGS_LIMITS,
  TIMEZONE_SUGGESTIONS,
  URL_PATTERN,
} from '../../../types/tenantSettings';
import type {
  TenantDto,
  UpdateTenantSettingsRequest,
} from '../../../types/tenantSettings';

interface FormState extends VehicleSettingsForm {
  name: string;
  street: string;
  streetNumber: string;
  city: string;
  postalCode: string;
  country: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  uidNumber: string;
  iban: string;
  bankName: string;
  companyRegistration: string;
  websiteUrl: string;
  logoUrl: string;
  primaryColor: string;
  accentColor: string;
  timeZone: string;
  language: string;
  /** Stored as percent string for input UX (0..100). */
  defaultVatRate: string;
  defaultPaymentTermDays: string;
  defaultOfferValidityDays: string;
  invoiceNumberPrefix: string;
  offerNumberPrefix: string;
  invoiceNumberStart: string;
  invoiceNumberResetYearly: boolean;
  offerNumberStart: string;
  offerNumberResetYearly: boolean;
  personalNumberPrefix: string;
  personalNumberSeparator: string;
  personalNumberIncludeYear: boolean;
  personalNumberSequenceLength: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

/** Keys of FormState whose value is a string (used by length/required validators). */
type StringFormKey = {
  [K in keyof FormState]: FormState[K] extends string ? K : never;
}[keyof FormState];

const EMPTY_FORM: FormState = {
  name: '',
  street: '',
  streetNumber: '',
  city: '',
  postalCode: '',
  country: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  uidNumber: '',
  iban: '',
  bankName: '',
  companyRegistration: '',
  websiteUrl: '',
  logoUrl: '',
  primaryColor: '',
  accentColor: '',
  timeZone: '',
  language: '',
  defaultVatRate: '',
  defaultPaymentTermDays: '',
  defaultOfferValidityDays: '',
  invoiceNumberPrefix: '',
  offerNumberPrefix: '',
  invoiceNumberStart: '',
  invoiceNumberResetYearly: true,
  offerNumberStart: '',
  offerNumberResetYearly: true,
  personalNumberPrefix: '',
  personalNumberSeparator: '',
  personalNumberIncludeYear: true,
  personalNumberSequenceLength: '',
  ...DEFAULT_VEHICLE_SETTINGS,
};

function fromTenant(tenant: TenantDto): FormState {
  return {
    name: tenant.name ?? '',
    street: tenant.address?.street ?? '',
    streetNumber: tenant.address?.streetNumber ?? '',
    city: tenant.address?.city ?? '',
    postalCode: tenant.address?.postalCode ?? '',
    country: tenant.address?.country ?? '',
    firstName: tenant.contactPerson?.firstName ?? '',
    lastName: tenant.contactPerson?.lastName ?? '',
    email: tenant.contactPerson?.email ?? '',
    phone: tenant.contactPerson?.phone ?? '',
    uidNumber: tenant.uidNumber ?? '',
    iban: tenant.iban ?? '',
    bankName: tenant.bankName ?? '',
    companyRegistration: tenant.companyRegistration ?? '',
    websiteUrl: tenant.websiteUrl ?? '',
    logoUrl: tenant.logoUrl ?? '',
    primaryColor: tenant.primaryColor ?? '',
    accentColor: tenant.accentColor ?? '',
    timeZone: tenant.timeZone ?? '',
    language: tenant.language ?? '',
    defaultVatRate:
      tenant.defaultVatRate != null
        ? String(Math.round(tenant.defaultVatRate * 10000) / 100)
        : '',
    defaultPaymentTermDays:
      tenant.defaultPaymentTermDays != null ? String(tenant.defaultPaymentTermDays) : '',
    defaultOfferValidityDays:
      tenant.defaultOfferValidityDays != null ? String(tenant.defaultOfferValidityDays) : '',
    invoiceNumberPrefix: tenant.invoiceNumberPrefix ?? '',
    offerNumberPrefix: tenant.offerNumberPrefix ?? '',
    invoiceNumberStart:
      tenant.invoiceNumberStart != null ? String(tenant.invoiceNumberStart) : '',
    invoiceNumberResetYearly: tenant.invoiceNumberResetYearly ?? true,
    offerNumberStart:
      tenant.offerNumberStart != null ? String(tenant.offerNumberStart) : '',
    offerNumberResetYearly: tenant.offerNumberResetYearly ?? true,
    // Show the effective values (backend falls back to these defaults when unset),
    // so the preview reflects the actual scheme in use.
    personalNumberPrefix: tenant.personalNumberPrefix ?? 'MA',
    personalNumberSeparator: tenant.personalNumberSeparator ?? '-',
    personalNumberIncludeYear: tenant.personalNumberIncludeYear ?? true,
    personalNumberSequenceLength: tenant.personalNumberSequenceLength != null ? String(tenant.personalNumberSequenceLength) : '3',
    ...vehicleSettingsFromTenant(tenant),
  };
}

function nullableTrim(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** Renders a sample document number, e.g. "RE-260210", from prefix + start sequence. */
function exampleNumber(prefix: string, fallbackPrefix: string, start: string): string {
  const yy = String(new Date().getFullYear() % 100).padStart(2, '0');
  const seq = start.trim() === '' ? 1 : Number(start) || 1;
  return `${prefix || fallbackPrefix}${yy}${String(seq).padStart(4, '0')}`;
}

/** Renders a sample personal number, e.g. "MA-2026-001", from the configured schema. */
function examplePersonalNumber(form: FormState): string {
  const prefix = form.personalNumberPrefix.trim() || 'MA';
  const sep = form.personalNumberSeparator;
  const len = (() => {
    const n = Number(form.personalNumberSequenceLength);
    return Number.isFinite(n) && n >= 1 && n <= 10 ? n : 3;
  })();
  const head = form.personalNumberIncludeYear
    ? `${prefix}${sep}${new Date().getFullYear()}${sep}`
    : `${prefix}${sep}`;
  return `${head}${String(1).padStart(len, '0')}`;
}

function toRequest(form: FormState): UpdateTenantSettingsRequest {
  const vatPercent = form.defaultVatRate.trim();
  const vatRate = vatPercent === '' ? null : Number(vatPercent) / 100;
  const paymentDays = form.defaultPaymentTermDays.trim();
  const offerDays = form.defaultOfferValidityDays.trim();

  return {
    name: form.name.trim(),
    address: {
      street: form.street.trim(),
      streetNumber: nullableTrim(form.streetNumber),
      city: form.city.trim(),
      postalCode: form.postalCode.trim(),
      country: form.country.trim(),
    },
    contactPerson: {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: nullableTrim(form.phone),
    },
    uidNumber: nullableTrim(form.uidNumber),
    iban: nullableTrim(form.iban),
    bankName: nullableTrim(form.bankName),
    companyRegistration: nullableTrim(form.companyRegistration),
    websiteUrl: nullableTrim(form.websiteUrl),
    logoUrl: nullableTrim(form.logoUrl),
    primaryColor: nullableTrim(form.primaryColor),
    accentColor: nullableTrim(form.accentColor),
    timeZone: nullableTrim(form.timeZone),
    language: nullableTrim(form.language),
    defaultVatRate: vatRate,
    defaultPaymentTermDays: paymentDays === '' ? null : Number(paymentDays),
    defaultOfferValidityDays: offerDays === '' ? null : Number(offerDays),
    invoiceNumberPrefix: nullableTrim(form.invoiceNumberPrefix),
    offerNumberPrefix: nullableTrim(form.offerNumberPrefix),
    invoiceNumberStart:
      form.invoiceNumberStart.trim() === '' ? null : Number(form.invoiceNumberStart),
    invoiceNumberResetYearly: form.invoiceNumberResetYearly,
    offerNumberStart:
      form.offerNumberStart.trim() === '' ? null : Number(form.offerNumberStart),
    offerNumberResetYearly: form.offerNumberResetYearly,
    personalNumberPrefix: nullableTrim(form.personalNumberPrefix),
    // why: an empty separator is a valid choice (e.g. "MA2026001"), so send the value
    // verbatim instead of nulling it out when blank.
    personalNumberSeparator: form.personalNumberSeparator,
    personalNumberIncludeYear: form.personalNumberIncludeYear,
    personalNumberSequenceLength: form.personalNumberSequenceLength.trim() === '' ? null : Number(form.personalNumberSequenceLength),
    ...vehicleSettingsPayload(form),
  };
}

export default function TenantSettingsPage() {
  const { t } = useTranslation(['tenantSettings', 'common']);
  const policyT = (key: string, options?: Record<string, unknown>) =>
    t(`tenantSettings:timeClockPolicy.${key}`, options);
  const navigate = useNavigate();
  const tenant = useTenantSettingsStore((s) => s.tenant);
  const loading = useTenantSettingsStore((s) => s.loading);
  const saving = useTenantSettingsStore((s) => s.saving);
  const error = useTenantSettingsStore((s) => s.error);
  const fetchTenant = useTenantSettingsStore((s) => s.fetchTenant);
  const updateTenant = useTenantSettingsStore((s) => s.updateTenant);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [logoError, setLogoError] = useState(false);

  const policy = useTimeClockPolicyStore((s) => s.policy);
  const policyLoading = useTimeClockPolicyStore((s) => s.loading);
  const policySaving = useTimeClockPolicyStore((s) => s.saving);
  const policyError = useTimeClockPolicyStore((s) => s.error);
  const fetchPolicy = useTimeClockPolicyStore((s) => s.fetchPolicy);
  const updatePolicy = useTimeClockPolicyStore((s) => s.updatePolicy);
  const [policyForm, setPolicyForm] = useState<PolicyForm | null>(null);
  const [policyErrors, setPolicyErrors] = useState<PolicyErrors>({});

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  useEffect(() => {
    void fetchPolicy({ force: true });
  }, [fetchPolicy]);

  useEffect(() => {
    if (policy) {
      setPolicyForm(policyToForm(policy));
      setPolicyErrors({});
    }
  }, [policy]);

  useEffect(() => {
    if (tenant) {
      setForm(fromTenant(tenant));
      setErrors({});
      setLogoError(false);
    }
  }, [tenant]);

  const isTenantDirty = useMemo(() => {
    if (!tenant) return false;
    const baseline = fromTenant(tenant);
    return (Object.keys(baseline) as (keyof FormState)[]).some(
      (key) => baseline[key] !== form[key]
    );
  }, [form, tenant]);

  const isPolicyDirty = useMemo(
    () => !!policy && !!policyForm && !policyFormsEqual(policyToForm(policy), policyForm),
    [policy, policyForm]
  );

  const isDirty = isTenantDirty || isPolicyDirty;

  useBeforeUnload(
    (event) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    }
  );

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
    if (key === 'logoUrl') setLogoError(false);
  };

  const validate = (): boolean => {
    const next: FormErrors = {};
    const L = TENANT_SETTINGS_LIMITS;

    const requireField = (key: StringFormKey, max?: number) => {
      const raw = form[key].trim();
      if (!raw) {
        next[key] = t('tenantSettings:validation.required');
      } else if (max != null && raw.length > max) {
        next[key] = t('tenantSettings:validation.tooLong', { count: max });
      }
    };

    const optionalLength = (key: StringFormKey, max: number) => {
      const raw = form[key].trim();
      if (raw && raw.length > max) {
        next[key] = t('tenantSettings:validation.tooLong', { count: max });
      }
    };

    requireField('name', L.name);
    requireField('street', L.street);
    optionalLength('streetNumber', L.streetNumber);
    requireField('city', L.city);
    requireField('postalCode', L.postalCode);
    requireField('country', L.country);
    requireField('firstName', L.firstName);
    requireField('lastName', L.lastName);
    requireField('email', L.email);
    if (!next.email && !EMAIL_PATTERN.test(form.email.trim())) {
      next.email = t('tenantSettings:validation.emailInvalid');
    }
    optionalLength('phone', L.phone);

    optionalLength('uidNumber', L.uidNumber);
    optionalLength('iban', L.iban);
    optionalLength('bankName', L.bankName);
    optionalLength('companyRegistration', L.companyRegistration);
    optionalLength('websiteUrl', L.websiteUrl);
    if (form.websiteUrl.trim() && !URL_PATTERN.test(form.websiteUrl.trim())) {
      next.websiteUrl = t('tenantSettings:validation.urlInvalid');
    }
    optionalLength('logoUrl', L.logoUrl);
    if (form.logoUrl.trim() && !URL_PATTERN.test(form.logoUrl.trim())) {
      next.logoUrl = t('tenantSettings:validation.urlInvalid');
    }
    if (form.primaryColor.trim() && !HEX_COLOR_PATTERN.test(form.primaryColor.trim())) {
      next.primaryColor = t('tenantSettings:validation.hexInvalid');
    }
    if (form.accentColor.trim() && !HEX_COLOR_PATTERN.test(form.accentColor.trim())) {
      next.accentColor = t('tenantSettings:validation.hexInvalid');
    }
    optionalLength('timeZone', L.timeZone);
    optionalLength('language', L.language);

    if (form.defaultVatRate.trim() !== '') {
      const v = Number(form.defaultVatRate);
      if (!Number.isFinite(v) || v < 0 || v > 100) {
        next.defaultVatRate = t('tenantSettings:validation.vatRange');
      }
    }
    if (form.defaultPaymentTermDays.trim() !== '') {
      const v = Number(form.defaultPaymentTermDays);
      if (!Number.isFinite(v) || v < 0) {
        next.defaultPaymentTermDays = t('tenantSettings:validation.nonNegative');
      }
    }
    if (form.defaultOfferValidityDays.trim() !== '') {
      const v = Number(form.defaultOfferValidityDays);
      if (!Number.isFinite(v) || v < 0) {
        next.defaultOfferValidityDays = t('tenantSettings:validation.nonNegative');
      }
    }
    optionalLength('invoiceNumberPrefix', L.invoiceNumberPrefix);
    optionalLength('offerNumberPrefix', L.offerNumberPrefix);

    const validateStart = (key: 'invoiceNumberStart' | 'offerNumberStart') => {
      const raw = form[key].trim();
      if (raw === '') return;
      const v = Number(raw);
      if (!Number.isInteger(v) || v < NUMBER_START_MIN || v > NUMBER_START_MAX) {
        next[key] = t('tenantSettings:validation.numberStartRange', {
          min: NUMBER_START_MIN,
          max: NUMBER_START_MAX,
        });
      }
    };
    validateStart('invoiceNumberStart');
    validateStart('offerNumberStart');

    optionalLength('personalNumberPrefix', L.personalNumberPrefix);
    optionalLength('personalNumberSeparator', L.personalNumberSeparator);
    if (form.personalNumberSequenceLength.trim() !== '') {
      const v = Number(form.personalNumberSequenceLength);
      if (
        !Number.isInteger(v) ||
        v < PERSONAL_NUMBER_SEQUENCE_MIN ||
        v > PERSONAL_NUMBER_SEQUENCE_MAX
      ) {
        next.personalNumberSequenceLength = t('tenantSettings:validation.sequenceLengthRange', {
          min: PERSONAL_NUMBER_SEQUENCE_MIN,
          max: PERSONAL_NUMBER_SEQUENCE_MAX,
        });
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handlePolicyChange = (next: PolicyForm) => {
    setPolicyForm(next);
    if (hasPolicyErrors(policyErrors)) {
      setPolicyErrors(validatePolicyForm(next, policyT));
    }
  };

  const validatePolicy = (): boolean => {
    if (!isPolicyDirty || !policyForm) return true;
    const next = validatePolicyForm(policyForm, policyT);
    setPolicyErrors(next);
    return !hasPolicyErrors(next);
  };

  const handleSave = async () => {
    const tenantValid = !isTenantDirty || validate();
    const policyValid = validatePolicy();
    if (!tenantValid || !policyValid) return;
    if (isTenantDirty) await updateTenant(toRequest(form));
    if (isPolicyDirty && policyForm) await updatePolicy(formToPolicy(policyForm));
  };

  const handleDiscard = () => {
    if (tenant) {
      setForm(fromTenant(tenant));
      setErrors({});
      setLogoError(false);
    }
    if (policy) {
      setPolicyForm(policyToForm(policy));
      setPolicyErrors({});
    }
  };

  if (loading && !tenant) return <Loading />;

  if (error && !tenant) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/settings')}
          leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
        >
          {t('common:actions.back')}
        </Button>
        <Card>
          <EmptyState
            variant="error"
            title={t('tenantSettings:errors.fetchFailed')}
            onRetry={fetchTenant}
          />
        </Card>
      </div>
    );
  }

  if (!tenant) return null;

  const primaryPreview = HEX_COLOR_PATTERN.test(form.primaryColor) ? form.primaryColor : '#00A871';
  const accentPreview = HEX_COLOR_PATTERN.test(form.accentColor) ? form.accentColor : '#48BFE3';

  // why: invoice/offer branding (logo, colors) is a Professional+ feature — mirrors the
  // backend IncludesBranding flag (Starter=false). Starter tenants see the fields but disabled,
  // with a tooltip explaining the upgrade. Tier values: Starter=1, Professional=2, Enterprise=3.
  const canUseBranding = Number(tenant.businessPlan ?? 0) >= 2;

  return (
    <div className="space-y-4 sm:space-y-6 pb-24">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => navigate('/settings')}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('common:actions.back')}
      </Button>

      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
          {t('tenantSettings:title')}
        </h1>
        <p className="text-[13px] text-slate-500 mt-0.5">
          {t('tenantSettings:subtitle')}
        </p>
      </div>

      {/* Company / Identity */}
      <Section
        title={t('tenantSettings:sections.company.title')}
        description={t('tenantSettings:sections.company.description')}
      >
        <Grid>
          <FormField
            label={t('tenantSettings:fields.name')}
            required
            value={form.name}
            maxLength={TENANT_SETTINGS_LIMITS.name}
            onChange={(e) => setField('name', e.target.value)}
            error={errors.name}
          />
          <FormField
            label={t('tenantSettings:fields.domain')}
            value={tenant.domain}
            readOnly
            disabled
            helperText={t('tenantSettings:hints.domainReadonly')}
          />
          <FormField
            label={t('tenantSettings:fields.businessPlan')}
            helperText={t('tenantSettings:hints.businessPlanReadonly')}
          >
            <div className="px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200">
              <Badge variant="info">{tenant.businessPlan}</Badge>
            </div>
          </FormField>
          <FormField
            label={t('tenantSettings:fields.isActive')}
            helperText={t('tenantSettings:hints.isActiveReadonly')}
          >
            <div className="px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200">
              <Badge variant={tenant.isActive ? 'success' : 'danger'}>
                {tenant.isActive
                  ? t('tenantSettings:status.active')
                  : t('tenantSettings:status.inactive')}
              </Badge>
            </div>
          </FormField>
        </Grid>
      </Section>

      {/* Owner */}
      <Section
        title={t('tenantSettings:sections.owner.title')}
        description={t('tenantSettings:sections.owner.description')}
      >
        <Grid>
          <FormField
            label={t('tenantSettings:fields.firstName')}
            required
            value={form.firstName}
            maxLength={TENANT_SETTINGS_LIMITS.firstName}
            onChange={(e) => setField('firstName', e.target.value)}
            error={errors.firstName}
          />
          <FormField
            label={t('tenantSettings:fields.lastName')}
            required
            value={form.lastName}
            maxLength={TENANT_SETTINGS_LIMITS.lastName}
            onChange={(e) => setField('lastName', e.target.value)}
            error={errors.lastName}
          />
          <FormField
            label={t('tenantSettings:fields.email')}
            type="email"
            required
            value={form.email}
            maxLength={TENANT_SETTINGS_LIMITS.email}
            onChange={(e) => setField('email', e.target.value)}
            error={errors.email}
          />
          <FormField
            label={t('tenantSettings:fields.phone')}
            value={form.phone}
            maxLength={TENANT_SETTINGS_LIMITS.phone}
            onChange={(e) => setField('phone', e.target.value)}
            error={errors.phone}
          />
        </Grid>
      </Section>

      {/* Address */}
      <Section
        title={t('tenantSettings:sections.address.title')}
        description={t('tenantSettings:sections.address.description')}
      >
        <Grid>
          <FormField
            label={t('tenantSettings:fields.street')}
            required
            value={form.street}
            maxLength={TENANT_SETTINGS_LIMITS.street}
            onChange={(e) => setField('street', e.target.value)}
            error={errors.street}
          />
          <FormField
            label={t('tenantSettings:fields.streetNumber')}
            value={form.streetNumber}
            maxLength={TENANT_SETTINGS_LIMITS.streetNumber}
            onChange={(e) => setField('streetNumber', e.target.value)}
            error={errors.streetNumber}
          />
          <FormField
            label={t('tenantSettings:fields.postalCode')}
            required
            value={form.postalCode}
            maxLength={TENANT_SETTINGS_LIMITS.postalCode}
            onChange={(e) => setField('postalCode', e.target.value)}
            error={errors.postalCode}
          />
          <FormField
            label={t('tenantSettings:fields.city')}
            required
            value={form.city}
            maxLength={TENANT_SETTINGS_LIMITS.city}
            onChange={(e) => setField('city', e.target.value)}
            error={errors.city}
          />
          <FormField
            label={t('tenantSettings:fields.country')}
            required
            value={form.country}
            maxLength={TENANT_SETTINGS_LIMITS.country}
            onChange={(e) => setField('country', e.target.value)}
            error={errors.country}
          />
        </Grid>
      </Section>

      {/* Legal */}
      <Section
        title={t('tenantSettings:sections.legal.title')}
        description={t('tenantSettings:sections.legal.description')}
      >
        <Grid>
          <FormField
            label={t('tenantSettings:fields.uidNumber')}
            placeholder={t('tenantSettings:placeholders.uidNumber')}
            value={form.uidNumber}
            maxLength={TENANT_SETTINGS_LIMITS.uidNumber}
            onChange={(e) => setField('uidNumber', e.target.value)}
            error={errors.uidNumber}
          />
          <FormField
            label={t('tenantSettings:fields.iban')}
            placeholder={t('tenantSettings:placeholders.iban')}
            value={form.iban}
            maxLength={TENANT_SETTINGS_LIMITS.iban}
            onChange={(e) => setField('iban', e.target.value)}
            error={errors.iban}
          />
          <FormField
            label={t('tenantSettings:fields.bankName')}
            value={form.bankName}
            maxLength={TENANT_SETTINGS_LIMITS.bankName}
            onChange={(e) => setField('bankName', e.target.value)}
            error={errors.bankName}
          />
          <FormField
            label={t('tenantSettings:fields.companyRegistration')}
            placeholder={t('tenantSettings:placeholders.companyRegistration')}
            value={form.companyRegistration}
            maxLength={TENANT_SETTINGS_LIMITS.companyRegistration}
            onChange={(e) => setField('companyRegistration', e.target.value)}
            error={errors.companyRegistration}
          />
          <FormField
            label={t('tenantSettings:fields.websiteUrl')}
            placeholder={t('tenantSettings:placeholders.websiteUrl')}
            value={form.websiteUrl}
            type="url"
            maxLength={TENANT_SETTINGS_LIMITS.websiteUrl}
            onChange={(e) => setField('websiteUrl', e.target.value)}
            error={errors.websiteUrl}
          />
        </Grid>
      </Section>

      {/* Branding */}
      <Section
        title={t('tenantSettings:sections.branding.title')}
        description={t('tenantSettings:sections.branding.description')}
      >
        {!canUseBranding && (
          <Tooltip content={t('tenantSettings:hints.brandingProfessionalOnly')}>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
              <Lock className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
              {t('tenantSettings:badges.professionalRequired')}
            </span>
          </Tooltip>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <FormField
              label={t('tenantSettings:fields.logoUrl')}
              placeholder={t('tenantSettings:placeholders.logoUrl')}
              value={form.logoUrl}
              type="url"
              maxLength={TENANT_SETTINGS_LIMITS.logoUrl}
              onChange={(e) => setField('logoUrl', e.target.value)}
              error={errors.logoUrl}
              disabled={!canUseBranding}
              helperText={t('tenantSettings:hints.logoUploadComing')}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ColorField
                label={t('tenantSettings:fields.primaryColor')}
                value={form.primaryColor}
                onChange={(v) => setField('primaryColor', v)}
                error={errors.primaryColor}
                disabled={!canUseBranding}
                helperText={t('tenantSettings:hints.colorFormat')}
              />
              <ColorField
                label={t('tenantSettings:fields.accentColor')}
                value={form.accentColor}
                onChange={(v) => setField('accentColor', v)}
                error={errors.accentColor}
                disabled={!canUseBranding}
                helperText={t('tenantSettings:hints.colorFormat')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <span className="block text-[12px] font-medium text-slate-600">
              {t('tenantSettings:hints.preview')}
            </span>
            <div
              className="rounded-xl border border-slate-200 overflow-hidden"
              style={{ borderTop: `4px solid ${primaryPreview}` }}
            >
              <div className="p-4 flex items-center gap-4 bg-white">
                <LogoPreview
                  url={form.logoUrl.trim()}
                  errored={logoError}
                  onError={() => setLogoError(true)}
                  noLogoLabel={t('tenantSettings:messages.noLogo')}
                  errorLabel={t('tenantSettings:messages.logoPreviewError')}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold text-slate-900 truncate">
                    {form.name || tenant.name}
                  </div>
                  <div className="text-[12px] text-slate-500 truncate">
                    {[form.street, form.streetNumber].filter(Boolean).join(' ')}
                    {form.postalCode || form.city ? ' · ' : ''}
                    {[form.postalCode, form.city].filter(Boolean).join(' ')}
                  </div>
                </div>
                <span
                  className="px-2.5 py-1 rounded-full text-[11px] font-medium text-white tabular-nums"
                  style={{ backgroundColor: accentPreview }}
                >
                  {form.offerNumberPrefix || 'ANG-'}260012
                </span>
              </div>
              <div
                className="px-4 py-2 text-[11px] text-white"
                style={{ backgroundColor: primaryPreview }}
              >
                {t('tenantSettings:messages.previewMockup')}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Locale */}
      <Section
        title={t('tenantSettings:sections.locale.title')}
        description={t('tenantSettings:sections.locale.description')}
      >
        <Grid>
          <FormField
            label={t('tenantSettings:fields.timeZone')}
            error={errors.timeZone}
          >
            <input
              list="tenant-tz-options"
              value={form.timeZone}
              maxLength={TENANT_SETTINGS_LIMITS.timeZone}
              placeholder={t('tenantSettings:placeholders.selectTimeZone')}
              onChange={(e) => setField('timeZone', e.target.value)}
              className={inputClass(!!errors.timeZone)}
            />
            <datalist id="tenant-tz-options">
              {TIMEZONE_SUGGESTIONS.map((tz) => (
                <option key={tz} value={tz} />
              ))}
            </datalist>
          </FormField>
          <FormField
            label={t('tenantSettings:fields.language')}
            error={errors.language}
          >
            <select
              value={form.language}
              onChange={(e) => setField('language', e.target.value)}
              className={inputClass(!!errors.language)}
            >
              <option value="">{t('tenantSettings:placeholders.selectLanguage')}</option>
              {LANGUAGE_SUGGESTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.value})
                </option>
              ))}
            </select>
          </FormField>
        </Grid>
      </Section>

      {/* Defaults */}
      <Section
        title={t('tenantSettings:sections.defaults.title')}
        description={t('tenantSettings:sections.defaults.description')}
      >
        <Grid>
          <FormField
            label={t('tenantSettings:fields.defaultVatRate')}
            type="number"
            inputMode="decimal"
            step="0.1"
            min={0}
            max={100}
            value={form.defaultVatRate}
            onChange={(e) => setField('defaultVatRate', e.target.value)}
            error={errors.defaultVatRate}
            helperText={t('tenantSettings:hints.vatRateUnit')}
          />
          <FormField
            label={t('tenantSettings:fields.defaultPaymentTermDays')}
            type="number"
            inputMode="numeric"
            min={0}
            value={form.defaultPaymentTermDays}
            onChange={(e) => setField('defaultPaymentTermDays', e.target.value)}
            error={errors.defaultPaymentTermDays}
            helperText={t('tenantSettings:hints.daysUnit')}
          />
          <FormField
            label={t('tenantSettings:fields.defaultOfferValidityDays')}
            type="number"
            inputMode="numeric"
            min={0}
            value={form.defaultOfferValidityDays}
            onChange={(e) => setField('defaultOfferValidityDays', e.target.value)}
            error={errors.defaultOfferValidityDays}
            helperText={t('tenantSettings:hints.daysUnit')}
          />
          <FormField
            label={t('tenantSettings:fields.invoiceNumberPrefix')}
            placeholder={t('tenantSettings:placeholders.invoicePrefix')}
            value={form.invoiceNumberPrefix}
            maxLength={TENANT_SETTINGS_LIMITS.invoiceNumberPrefix}
            onChange={(e) => setField('invoiceNumberPrefix', e.target.value)}
            error={errors.invoiceNumberPrefix}
            helperText={exampleNumber(form.invoiceNumberPrefix, 'RE-', form.invoiceNumberStart)}
          />
          <FormField
            label={t('tenantSettings:fields.invoiceNumberStart')}
            type="number"
            inputMode="numeric"
            min={NUMBER_START_MIN}
            max={NUMBER_START_MAX}
            placeholder={t('tenantSettings:placeholders.numberStart')}
            value={form.invoiceNumberStart}
            onChange={(e) => setField('invoiceNumberStart', e.target.value)}
            error={errors.invoiceNumberStart}
            helperText={t('tenantSettings:hints.numberStart')}
          />
          <FormField label={t('tenantSettings:fields.invoiceNumberResetYearly')}>
            <Checkbox label={t('tenantSettings:hints.resetYearly')} checked={form.invoiceNumberResetYearly} onChange={(v) => setField('invoiceNumberResetYearly', v)} />
          </FormField>
          <FormField
            label={t('tenantSettings:fields.offerNumberPrefix')}
            placeholder={t('tenantSettings:placeholders.offerPrefix')}
            value={form.offerNumberPrefix}
            maxLength={TENANT_SETTINGS_LIMITS.offerNumberPrefix}
            onChange={(e) => setField('offerNumberPrefix', e.target.value)}
            error={errors.offerNumberPrefix}
            helperText={exampleNumber(form.offerNumberPrefix, 'ANG-', form.offerNumberStart)}
          />
          <FormField
            label={t('tenantSettings:fields.offerNumberStart')}
            type="number"
            inputMode="numeric"
            min={NUMBER_START_MIN}
            max={NUMBER_START_MAX}
            placeholder={t('tenantSettings:placeholders.numberStart')}
            value={form.offerNumberStart}
            onChange={(e) => setField('offerNumberStart', e.target.value)}
            error={errors.offerNumberStart}
            helperText={t('tenantSettings:hints.numberStart')}
          />
          <FormField label={t('tenantSettings:fields.offerNumberResetYearly')}>
            <Checkbox label={t('tenantSettings:hints.resetYearly')} checked={form.offerNumberResetYearly} onChange={(v) => setField('offerNumberResetYearly', v)} />
          </FormField>
        </Grid>
      </Section>

      {/* Personal number scheme */}
      <Section
        title={t('tenantSettings:sections.personalNumber.title')}
        description={t('tenantSettings:sections.personalNumber.description')}
      >
        <Grid>
          <FormField
            label={t('tenantSettings:fields.personalNumberPrefix')}
            placeholder="MA"
            value={form.personalNumberPrefix}
            maxLength={TENANT_SETTINGS_LIMITS.personalNumberPrefix}
            onChange={(e) => setField('personalNumberPrefix', e.target.value)}
            error={errors.personalNumberPrefix}
          />
          <FormField
            label={t('tenantSettings:fields.personalNumberSeparator')}
            placeholder="-"
            value={form.personalNumberSeparator}
            maxLength={TENANT_SETTINGS_LIMITS.personalNumberSeparator}
            onChange={(e) => setField('personalNumberSeparator', e.target.value)}
            error={errors.personalNumberSeparator}
            helperText={t('tenantSettings:hints.personalNumberSeparator')}
          />
          <FormField
            label={t('tenantSettings:fields.personalNumberSequenceLength')}
            type="number"
            inputMode="numeric"
            min={PERSONAL_NUMBER_SEQUENCE_MIN}
            max={PERSONAL_NUMBER_SEQUENCE_MAX}
            value={form.personalNumberSequenceLength}
            onChange={(e) => setField('personalNumberSequenceLength', e.target.value)}
            error={errors.personalNumberSequenceLength}
            helperText={t('tenantSettings:hints.personalNumberSequenceLength')}
          />
          <FormField label={t('tenantSettings:fields.personalNumberIncludeYear')}>
            <label className="flex items-center gap-2 mt-1 text-[13px] text-slate-700">
              <input
                type="checkbox"
                checked={form.personalNumberIncludeYear}
                onChange={(e) => setField('personalNumberIncludeYear', e.target.checked)}
                className="rounded border-slate-300"
              />
              {t('tenantSettings:hints.personalNumberIncludeYear')}
            </label>
          </FormField>
          <FormField label={t('tenantSettings:fields.personalNumberExample')}>
            <div className="px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200">
              <span className="font-mono-num text-[13px] text-slate-900 tabular-nums">
                {examplePersonalNumber(form)}
              </span>
            </div>
          </FormField>
        </Grid>
      </Section>

      {/* Time clock policy */}
      <Section
        title={t('tenantSettings:timeClockPolicy.title')}
        description={t('tenantSettings:timeClockPolicy.description')}
      >
        {policyForm ? (
          <TimeClockPolicySection
            form={policyForm}
            errors={policyErrors}
            onChange={handlePolicyChange}
          />
        ) : policyError && !policyLoading ? (
          <EmptyState
            variant="error"
            title={t(policyError)}
            onRetry={() => fetchPolicy({ force: true })}
          />
        ) : (
          <Loading />
        )}
      </Section>
      <VehicleSettingsSection values={form} onChange={setField} />

      {/* Sticky save bar */}
      <div
        className="fixed bottom-0 left-0 right-0 lg:left-[212px] bg-white/95 backdrop-blur border-t border-slate-200 px-4 sm:px-6 py-3 z-20"
      >
        <div className="flex items-center justify-between gap-3 max-w-6xl mx-auto">
          <span className="text-[12px] text-slate-500">
            {isDirty ? t('tenantSettings:messages.unsavedChangesTitle') : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDiscard}
              disabled={!isDirty || saving || policySaving}
              leftIcon={<RotateCcw className="w-4 h-4" strokeWidth={2} />}
            >
              {t('tenantSettings:actions.discard')}
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || saving || policySaving}
              leftIcon={<Save className="w-4 h-4" strokeWidth={2} />}
            >
              {saving || policySaving ? t('common:actions.saving') : t('tenantSettings:actions.save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function inputClass(hasError: boolean) {
  return [
    'w-full px-3 py-1.5 rounded-md bg-white border text-[13px] text-slate-900',
    'placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-colors',
    hasError
      ? 'border-red-500 focus:ring-red-500/30'
      : 'border-slate-200 focus:ring-[#00A871]/30 focus:border-[#00A871]/50',
  ]
    .filter(Boolean)
    .join(' ');
}

function ColorField({
  label,
  value,
  onChange,
  error,
  helperText,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  helperText?: string;
  disabled?: boolean;
}) {
  const isValid = HEX_COLOR_PATTERN.test(value);
  const swatch = isValid ? value : '#FFFFFF';

  return (
    <FormField label={label} error={error} helperText={helperText}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={isValid ? value : '#000000'}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          disabled={disabled}
          className="h-9 w-12 rounded-md border border-slate-200 bg-white cursor-pointer p-1 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={label}
        />
        <input
          type="text"
          value={value}
          maxLength={TENANT_SETTINGS_LIMITS.color}
          placeholder="#00A871"
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={inputClass(!!error)}
        />
        <span
          className="h-9 w-9 rounded-md border border-slate-200 flex-shrink-0"
          style={{ backgroundColor: swatch }}
          aria-hidden="true"
        />
      </div>
    </FormField>
  );
}

function LogoPreview({
  url,
  errored,
  onError,
  noLogoLabel,
  errorLabel,
}: {
  url: string;
  errored: boolean;
  onError: () => void;
  noLogoLabel: string;
  errorLabel: string;
}) {
  if (!url) {
    return (
      <div className="w-16 h-16 rounded-md border border-dashed border-slate-300 flex items-center justify-center text-[11px] text-slate-500 text-center px-1">
        {noLogoLabel}
      </div>
    );
  }
  if (errored) {
    return (
      <div className="w-16 h-16 rounded-md border border-dashed border-red-300 flex items-center justify-center text-red-500" title={errorLabel}>
        <ImageOff className="w-6 h-6" strokeWidth={1.75} />
      </div>
    );
  }
  return (
    <img
      src={url}
      onError={onError}
      alt=""
      className="w-16 h-16 object-contain rounded-md bg-white border border-slate-200"
    />
  );
}
